import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, GitBranch, Share2, MessageSquare } from 'lucide-react';
import { SEO } from '../components/SEO';

const WHAT_YOU_GET = [
  { icon: GitBranch,    text: '30-min walkthrough of your actual codebase' },
  { icon: Share2,       text: 'Live architecture graph built from your repos' },
  { icon: MessageSquare, text: 'Q&A with the engineering team' },
];

export function DemoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', company: '', teamSize: '', message: '',
  });

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--s0)' }}>
      <SEO
        title="Request a Demo"
        description="Book a personalised demo of CodePilot Engineer. Bring your own repository and see AI-powered codebase intelligence working on your actual architecture."
        url="https://codepilot.engineer/demo"
      />

      {/* Minimal nav */}
      <nav className="border-b px-6 h-14 flex items-center" style={{ borderColor: 'var(--b-subtle)' }}>
        <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/images/codepilotlogo.png" alt="CodePilot Engineer" className="w-6 h-6 rounded-md object-contain" />
            <span className="text-sm font-semibold text-t1 tracking-tight">
              CodePilot <span className="text-t3">Engineer</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-t3 hover:text-t1 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-5 gap-12 md:gap-16 items-start">

          {/* Left — what you get */}
          <div className="md:col-span-2">
            <div className="tag mb-6">
              Request a demo
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-t1 tracking-tight leading-snug mb-4">
              See CodePilot Engineer working on your codebase
            </h1>
            <p className="text-sm text-t3 leading-relaxed mb-8">
              We'll schedule a personalised session where you can bring your own repository and ask questions about your actual architecture.
            </p>

            <div className="space-y-4">
              {WHAT_YOU_GET.map(item => (
                <div key={item.text} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: 'var(--overlay-sm)', border: '1px solid var(--b-default)' }}>
                    <item.icon className="w-3.5 h-3.5" style={{ color: 'var(--t1)' }} />
                  </div>
                  <span className="text-sm text-t2 leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t" style={{ borderColor: 'var(--b-subtle)' }}>
              <p className="text-xs text-t4 leading-relaxed">
                Typically replies within one business day. No sales pressure — just a focused engineering conversation.
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="md:col-span-3">
            {submitted ? (
              <div className="card p-8 text-center" style={{ animation: 'fadeIn 0.3s ease-out' }}>
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <h2 className="text-lg font-semibold text-t1 mb-2">Request received</h2>
                <p className="text-sm text-t3 leading-relaxed max-w-xs mx-auto mb-6">
                  We'll reach out to <span className="text-t2 font-medium">{form.email}</span> within one business day to schedule your session.
                </p>
                <Link to="/" className="btn-secondary text-sm px-5 py-2">
                  Back to home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="card p-6 md:p-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-t2 mb-1.5">Full name <span className="text-red-400">*</span></label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Arif Khan"
                      className="input-base placeholder-t4"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-t2 mb-1.5">Work email <span className="text-red-400">*</span></label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="arif@company.com"
                      className="input-base placeholder-t4"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-t2 mb-1.5">Company / team name <span className="text-red-400">*</span></label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    required
                    placeholder="StackFlow"
                    className="input-base placeholder-t4"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-t2 mb-1.5">Engineering team size</label>
                  <select
                    name="teamSize"
                    value={form.teamSize}
                    onChange={handleChange}
                    className="input-base"
                    style={{ cursor: 'pointer' }}>
                    <option value="">Select team size</option>
                    <option value="1-5">1 – 5 engineers</option>
                    <option value="6-15">6 – 15 engineers</option>
                    <option value="16-50">16 – 50 engineers</option>
                    <option value="50+">50+ engineers</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-t2 mb-1.5">What are you hoping to solve? <span className="text-t4 font-normal">(optional)</span></label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="E.g. onboarding new engineers, understanding service dependencies, reducing time spent on code review..."
                    className="input-base placeholder-t4 resize-none"
                  />
                </div>

                <button type="submit" className="btn-primary w-full justify-center py-2.5 text-sm">
                  Request demo
                </button>

                <p className="text-center text-xs text-t4">
                  No commitment required. We respect your privacy.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
